/**
 * Help Centre > Support & FAQ content.
 *
 * Source: "Beevelope Help Centre - Support & FAQ" (product doc). Each article
 * follows the document's own shape: a short "what it is" line, numbered steps,
 * and a Tip/Note where the document has one. Modules are in sidebar order.
 *
 * This is content, not configuration: edit the copy here and the Help Centre
 * updates. `helpContent.test.ts` checks the ids stay unique and anchor-safe and
 * that no table row drifts out of line with its header.
 */

export interface HelpCallout {
  tone: 'tip' | 'note'
  text: string
}

export interface HelpTableGroup {
  /** Sub-heading for a run of rows (e.g. the node palette category). */
  title?: string
  rows: string[][]
}

export interface HelpTable {
  columns: string[]
  groups: HelpTableGroup[]
}

export interface HelpArticle {
  /** Stable slug; also the settings deep-link anchor `help-<id>`. */
  id: string
  title: string
  whatItIs?: string
  steps?: string[]
  callout?: HelpCallout
  table?: HelpTable
}

export interface HelpModule {
  id: string
  title: string
  description?: string
  articles: HelpArticle[]
}

const NODE_COLUMNS = ['Node', 'What it does', 'Key settings']
const PROVIDER_COLUMNS = ['Provider', 'What connecting it does', 'You provide']

export const HELP_MODULES: HelpModule[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    description: 'Four steps take you from an empty account to your first live campaign.',
    articles: [
      {
        id: 'getting-started-first-campaign',
        title: 'From empty account to first campaign',
        steps: [
          'Connect a sending mailbox under Settings - Email Accounts (this is the inbox your emails send from).',
          'Add your contacts (manually, by importing a CSV/Excel file, or by pulling them from Apollo).',
          'Build a workflow (your email sequence) and set it to Active.',
          'Launch a campaign - pick the workflow, the sender mailbox, and the audience.',
        ],
      },
      {
        id: 'getting-started-ai-quality',
        title: 'Make the AI work better (recommended)',
        whatItIs: 'Beevelope writes and personalises emails for you. Two things make that output sharper:',
        steps: [
          'Upload your Materials (company decks, one-pagers, case studies) so the AI writes using your real messaging.',
          'Connect enrichment providers (e.g. Apollo) so contacts carry richer data for personalisation.',
        ],
        callout: {
          tone: 'tip',
          text: 'You can start sending before doing this - but adding materials and enrichment noticeably improves email quality.',
        },
      },
    ],
  },
  {
    id: 'contacts',
    title: 'Contacts',
    description: 'There are three ways to add contacts, plus tools to organise and enrich them.',
    articles: [
      {
        id: 'contacts-add-manually',
        title: 'Add a single contact manually',
        whatItIs: 'Create one contact by hand.',
        steps: [
          'On the Contacts page, click Add contact (top-right).',
          'Fill in the Essential section - Email is the only required field; First name and Last name are recommended.',
          'Optionally add Professional details (job title, company, industry, website) and expand Additional details for seniority, location, phone, company size, LinkedIn, etc.',
          'Click Save. The email must be unique - a duplicate will be rejected.',
        ],
      },
      {
        id: 'contacts-import-file',
        title: 'Import contacts from a CSV or Excel file',
        whatItIs: 'Bulk-add contacts from a spreadsheet.',
        steps: [
          'On the Contacts page, click Import.',
          'Download the CSV or Excel template if you want the exact column layout (optional).',
          'Upload your file (.csv, .xlsx, .xls, or .txt, up to 10 MB).',
          'On the Map fields step, check the auto-suggested column mapping and adjust it. At least one column must map to Email.',
          'Choose how to handle duplicates: Skip duplicates (default) or Update duplicates (merge new data into existing contacts).',
          "Click Import. You'll see a summary of imported, duplicate, and failed rows.",
        ],
        callout: {
          tone: 'tip',
          text: 'If a row fails, the result screen tells you why (usually a missing or malformed email).',
        },
      },
      {
        id: 'contacts-import-apollo',
        title: 'Import contacts from Apollo',
        whatItIs: "Search Apollo's B2B database and import prospects directly.",
        steps: [
          'Add your Apollo API key first in Settings - Integrations.',
          'On the Contacts page, click Sources, then Apollo search.',
          'Use the People search filters (job title, location, seniority, company, employee size, revenue, technologies, email status) and run the search. Searching is free.',
          'Click Reveal emails on the rows you want (this uses your Apollo credits). Only rows with a revealed email can be imported.',
          'Select the people to import, optionally apply tags and choose a destination list, then click Import. Duplicates are skipped automatically.',
        ],
        callout: {
          tone: 'note',
          text: 'Apollo is currently the only provider for pulling contacts directly. Other providers (Salesforce, HubSpot, Pipedrive, Hunter, etc.) are used for enrichment and CRM sync, not for pulling in new contacts.',
        },
      },
      {
        id: 'contacts-tags-and-lists',
        title: 'Organise contacts with tags and lists',
        whatItIs: 'Group and filter your contacts.',
        steps: [
          "Tags: select one or more contacts, use bulk actions to Tag/Untag. Tags are the main way to filter and to target a workflow's audience.",
          'Lists: select contacts and save them as a named list (or let an Apollo import create one). Use the Lists button to manage them and filter the table.',
          'Deleting a list does not delete the contacts in it.',
        ],
      },
      {
        id: 'contacts-enrich',
        title: 'Enrich contacts (fill in missing data)',
        whatItIs:
          'Research and complete contact or company details. Enrichment is a separate action - it does not run automatically on import.',
        steps: [
          'Select the contacts you want to enrich.',
          'Choose the fields to research across Person, Company, Financial, and News groups.',
          'Run enrichment - the app fills missing fields (and can verify emails) using your connected providers and AI.',
        ],
        callout: {
          tone: 'tip',
          text: 'Revealing an email in Apollo and enriching an existing contact both consume credits; file imports do not.',
        },
      },
    ],
  },
  {
    id: 'templates',
    title: 'Templates',
    description: 'The Templates page has two tabs: Email templates and Workflow templates - they are different things.',
    articles: [
      {
        id: 'templates-what-is-email-template',
        title: 'Understand email templates',
        whatItIs:
          "An email template here is a structure template - it defines the block-by-block skeleton and writing instructions the AI follows when it generates each email. It is not a drag-and-drop HTML design, and there are no {{merge tags}}: personalisation is written by the AI from each recipient's contact data.",
      },
      {
        id: 'templates-create-email-template',
        title: 'Create an email template',
        whatItIs: 'Build a reusable structure for the AI to write from.',
        steps: [
          'On the Templates page (Email templates tab), click Create template.',
          'Enter a Name, optional Description, a Category (Cold Outreach, Follow Up, Nurture, Re-engage, Introduction, or General), and a Subject instruction (guidance for the AI-written subject line).',
          'Optionally Start from preset to load blocks from a system template.',
          'Add blocks (1-10). For each block set a Block name and an Instruction (what that block should say); under Advanced you can set max words, tone, and an example.',
          'Save. You can also Duplicate, Delete, or Set default from the list.',
        ],
        callout: {
          tone: 'note',
          text: 'System templates are read-only - duplicate one to make an editable copy.',
        },
      },
      {
        id: 'templates-use-in-workflow',
        title: 'Use a template in a workflow',
        whatItIs: 'Apply a template so the AI writes emails in that structure.',
        steps: [
          'Open your workflow and select a Send Email node.',
          "In the node's Content tab, pick your template from the Email Structure Template dropdown.",
          "When the campaign runs, the AI writes each email following that template's blocks, personalised to the recipient and grounded in any selected materials.",
          "If you don't choose a template, your default template is used.",
        ],
      },
      {
        id: 'templates-workflow-templates',
        title: 'Workflow templates',
        whatItIs:
          'The Workflow templates tab is a gallery of prebuilt automation sequences you can use to jump-start a new workflow, rather than building from scratch.',
      },
    ],
  },
  {
    id: 'materials',
    title: 'Materials',
    description: 'Your collateral, so the AI writes in your real messaging.',
    articles: [
      {
        id: 'materials-what-they-are',
        title: 'What materials are',
        whatItIs:
          'Materials are your marketing collateral (decks, one-pagers, case studies, web pages) that you give Beevelope so the AI can ground the emails and content it writes in your real messaging. Each material is tagged as Company or Competitor material. After upload, the app extracts the text, writes an AI summary and key points, and indexes it so it can be used when writing campaigns.',
      },
      {
        id: 'materials-add',
        title: 'Add materials',
        whatItIs: 'Upload files or import a web page.',
        steps: [
          'On the Materials page, click Upload.',
          'Upload files tab: drag and drop or browse. Supported types are PDF, DOC, DOCX, PPT, PPTX, TXT, MD (up to 20 MB each). You can upload several at once and edit each title before uploading.',
          'From URL tab: paste a website URL (and optional title), then Upload from URL - the app crawls and extracts the page content.',
          "Processing runs in the background; a material shows an 'Indexed' badge when it's ready to use.",
        ],
      },
      {
        id: 'materials-use-and-manage',
        title: 'Use and manage materials',
        whatItIs: 'Put materials to work and keep them organised.',
        steps: [
          'Select materials inside a Send Email workflow node so the AI writes using that collateral.',
          'Per material you can Rename, Preview, Analyze, Download, or Delete; filter by tag in the left sidebar.',
          'Content Insights aggregates all processed materials into an executive summary, strategies, ICP, and campaign suggestions.',
        ],
      },
    ],
  },
  {
    id: 'workflows',
    title: 'Workflows',
    description:
      'A workflow is the automated sequence that powers a campaign - the emails, the timing, the branching, and the actions. You build it on a visual canvas by dragging in nodes and connecting them.',
    articles: [
      {
        id: 'workflows-create',
        title: 'Create a workflow',
        whatItIs: 'Build your sequence on the canvas.',
        steps: [
          'Go to Workflows and click Create workflow (or start from a Workflow template).',
          'Every workflow begins with a Trigger node - it defines who enters the sequence.',
          'Drag nodes from the left palette onto the canvas and connect them in the order they should run.',
          'Click any node to open its settings panel and configure it.',
          'Save as you go. The node reference below explains each node.',
        ],
      },
      {
        id: 'workflows-activate',
        title: 'Activate a workflow',
        whatItIs: 'Make a workflow usable by a campaign.',
        steps: [
          "Set the workflow's status to Active.",
          'Only Active workflows appear when you launch a campaign.',
          "Beevelope checks the workflow for loops - a workflow that cycles back on itself can't be launched.",
        ],
      },
      {
        id: 'workflows-node-reference',
        title: 'Node reference',
        whatItIs:
          "What each block does and the main settings. Nodes are grouped in the palette by category. If a node needs an integration (e.g. SMS, CRM, meetings) that isn't connected, it safely skips or creates a manual task instead of breaking the workflow.",
        table: {
          columns: NODE_COLUMNS,
          groups: [
            {
              title: 'Triggers',
              rows: [
                [
                  'Trigger',
                  'Entry point - defines which contacts enter and starts the sequence.',
                  'Trigger type (manual/schedule); audience filters: tags, lead status, seniority, industry, company size.',
                ],
              ],
            },
            {
              title: 'Messages',
              rows: [
                [
                  'Send Email',
                  'Sends an email with open/click tracking - the core step. Can be AI-written or manual, and is reply-aware for follow-ups.',
                  'Mode: Manual (your subject/body) or AI Generated; template, materials, contact fields to personalise on, tone, purpose.',
                ],
                [
                  'Collaterals',
                  'Attaches a file to later emails - AI-generates a slide deck per contact, or attaches an uploaded material.',
                  'AI generate (slides, focus, tone, theme, logo) or attach an existing material.',
                ],
                [
                  'Send SMS',
                  "Sends a text message to the contact's phone (via Twilio).",
                  'Message template. Requires Twilio in Integrations.',
                ],
                [
                  'LinkedIn',
                  "Performs a LinkedIn outreach action; if LinkedIn isn't connected, creates a manual task instead.",
                  'Action type, message template.',
                ],
              ],
            },
            {
              title: 'Data',
              rows: [
                [
                  'Enrich Contacts',
                  'Researches and fills in contact/company data before later steps use it.',
                  'Which data to enrich (person, company, sector, signals, hooks); skip-already-enriched; max age.',
                ],
              ],
            },
            {
              title: 'Flow control',
              rows: [
                [
                  'Delay',
                  'Pauses the contact before the next step.',
                  'Duration (minutes/hours/days), Until a set time, or For an event (e.g. opened/clicked/replied) with a timeout.',
                ],
                [
                  'Condition',
                  'Branches the contact down a Yes or No path based on a test.',
                  'Email engagement (opened/clicked/replied), reply sentiment, lead or contact properties, timing, or a custom expression.',
                ],
                [
                  'A/B Test',
                  'Randomly splits contacts across variant paths by percentage, to test content or timing.',
                  'Variants with weighted percentages.',
                ],
                [
                  'Rate Limit',
                  'Throttles how fast the sequence sends.',
                  'Daily cap, hourly cap, or random delay between sends.',
                ],
                [
                  'Branch by Field',
                  'Routes the contact down the first matching branch based on field rules.',
                  'Ordered rules (field, operator, value) + optional default branch.',
                ],
                [
                  'AI Decision',
                  'Uses AI to pick the best branch for each contact.',
                  'Decision prompt + labelled branches.',
                ],
              ],
            },
            {
              title: 'Actions',
              rows: [
                [
                  'Action',
                  'Performs one back-office action on the contact.',
                  'Add/remove tag, move to list, update field; lead scoring; send webhook/notification; add to another campaign.',
                ],
                [
                  'Goal',
                  'Marks success - tags the contact when a goal is met; the flow continues either way.',
                  'Goal type: replied, clicked link, or booked meeting.',
                ],
                [
                  'Exit Workflow',
                  'Removes the contact from the workflow and cancels their pending steps.',
                  'Reason; optionally set lead status or add a tag.',
                ],
                [
                  'Create Task',
                  'Creates a sales follow-up task for the contact.',
                  'Task type, title/description, priority, due-in-days.',
                ],
                [
                  'Assign Owner',
                  'Assigns the contact to a team member (round-robin).',
                  'Team member list, assignment mode.',
                ],
              ],
            },
            {
              title: 'Notifications',
              rows: [
                [
                  'Team Alert',
                  'Sends an internal notification to your team about this contact.',
                  'Channel (email, Slack, or Teams), message template, include contact details.',
                ],
              ],
            },
            {
              title: 'Integrations',
              rows: [
                [
                  'CRM Sync',
                  'Pushes the contact to your CRM and optionally creates a deal.',
                  'Provider (HubSpot, Pipedrive, Salesforce); create-deal with name/stage. Requires the CRM in Integrations.',
                ],
                [
                  'Schedule Meeting',
                  'Creates a meeting / calendar event and stores the link on the contact.',
                  'Provider (Zoom, Google Calendar, Outlook, Calendly); topic, duration, attendees.',
                ],
                [
                  'HTTP Request',
                  'Calls an external API as part of the flow.',
                  'URL, method, headers, body, store-response.',
                ],
              ],
            },
          ],
        },
      },
    ],
  },
  {
    id: 'campaigns',
    title: 'Campaigns',
    description: 'A campaign runs a workflow against an audience from a chosen mailbox.',
    articles: [
      {
        id: 'campaigns-launch',
        title: 'Launch a campaign',
        whatItIs: 'A campaign runs a workflow against an audience from a chosen mailbox.',
        steps: [
          'On the Campaigns page, click Launch campaign.',
          'Step 1 - Workflow: choose an Active workflow (only active ones are shown).',
          'Step 2 - Sender: choose a verified sender mailbox (only verified mailboxes appear).',
          'Step 3 - Audience: choose All contacts or a specific contact list.',
          'Step 4 - Schedule & launch: name the campaign, set the start time (now or future), review the summary, and click Launch campaign.',
        ],
        callout: {
          tone: 'note',
          text: "If you schedule a future time, the campaign waits and launches automatically then - it won't send early.",
        },
      },
      {
        id: 'campaigns-prelaunch-checklist',
        title: 'Before you launch - checklist',
        whatItIs: 'Five things to confirm for a clean, deliverable send:',
        steps: [
          'Your workflow is built and set to Active (it stays active through launch).',
          'Your sender mailbox is Verified - and ideally warmed up, with SPF/DKIM/DMARC passing.',
          'The right audience is selected (all contacts vs. a specific list), and it has at least one contact.',
          "Sensible sending limits are set - via the mailbox's Daily Send Limit and/or a Rate Limit node in the workflow.",
          'The scheduled start time is correct.',
        ],
        callout: {
          tone: 'tip',
          text: "DNS (SPF/DKIM/DMARC) is checked at launch but only warns - it won't block you. Fix any DNS warnings for better inbox placement.",
        },
      },
      {
        id: 'campaigns-pause-resume',
        title: 'Pause, resume, and schedule',
        whatItIs: 'Control a campaign after it starts.',
        steps: [
          'A running campaign can be Paused and later Resumed.',
          'A future-dated campaign shows as Scheduled until its start time.',
        ],
      },
      {
        id: 'campaigns-approvals',
        title: 'How do approvals work?',
        whatItIs:
          'Representatives send workflows and campaigns to an Owner, Admin or Manager for review instead of activating or launching them directly. The approver reviews them under Settings and either approves them or sends them back with a reason, which the Representative sees on the item and under Settings - My Approval Requests.',
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Configurations & Integrations',
    description:
      'Connect your other tools so Beevelope can enrich contacts, sync to your CRM, book meetings, and send notifications.',
    articles: [
      {
        id: 'integrations-how-they-work',
        title: 'How integrations work',
        whatItIs:
          'Connect your other tools so Beevelope can enrich contacts, sync to your CRM, book meetings, and send notifications.',
        steps: [
          'Go to Settings - Integrations. Providers are grouped by category with a search box.',
          'Click a provider to expand it, paste the required credential(s) into the fields, optionally click Test to verify, then Save All Changes.',
          'A green dot means connected; saved keys show only the last 4 characters.',
          'LinkedIn uses a Connect with LinkedIn button (OAuth); every other provider is paste-a-credential.',
        ],
      },
      {
        id: 'integrations-connect-mailbox',
        title: 'Connect a sending mailbox (Email Accounts)',
        whatItIs: 'Add the inbox your campaigns send from. This uses SMTP/IMAP credentials.',
        steps: [
          'Go to Email Accounts and click Add Email Account.',
          'Step 1 - Basic info: Label, Email Address, and Display Name (the sender name recipients see).',
          'Step 2 - SMTP (required, for sending): SMTP server, Port (default 587), Username, Password (use an app password for Gmail/Outlook), and TLS or SSL.',
          'Step 3 - IMAP (optional, for reading replies): IMAP server, Port (default 993), username/password, and the auto-detect replies toggle. If left blank, SMTP settings are reused.',
          'Step 4 - Review: set the Daily Send Limit (default 500) and click Add & Verify. Only Verified mailboxes can be used in campaigns.',
        ],
        callout: {
          tone: 'note',
          text: 'You can set a per-mailbox email signature and mark a default mailbox from Email Accounts.',
        },
      },
      {
        id: 'integrations-warmup',
        title: 'Warm up a mailbox',
        whatItIs:
          "Warm-up gradually builds a new mailbox's sending reputation. It is handled by an external warm-up provider, connected per mailbox.",
        steps: [
          "Connect a warm-up provider (Instantly) using that provider's API key.",
          'Set the warm-up schedule (starting daily volume and target daily volume).',
          'Let it run for about 2-3 weeks before sending real campaign volume from that mailbox.',
        ],
        callout: {
          tone: 'tip',
          text: 'Keep warm-up running even after you go live - it protects deliverability over time.',
        },
      },
      {
        id: 'integrations-data-providers',
        title: 'Data & Enrichment providers',
        whatItIs: 'Find and enrich prospect and company data.',
        table: {
          columns: PROVIDER_COLUMNS,
          groups: [
            {
              rows: [
                ['Apollo.io', 'Find prospects, reveal/verify emails, pull saved lists.', 'API Key'],
                ['Hunter.io', 'Find and verify professional email addresses.', 'API Key'],
                ['LinkedIn', 'Profile enrichment (OAuth connect).', 'Client ID + Secret, then Connect'],
                ['People Data Labs', 'B2B person & company data enrichment.', 'API Key'],
                ['Explorium', 'AI-powered B2B prospect & business data.', 'API Key'],
                ['Financial Modeling Prep', 'Company financials and valuation metrics.', 'API Key'],
                ['Alpha Vantage', 'Stock fundamentals and company overview.', 'API Key'],
                ['NewsAPI.org', 'Real-time news for account/contact research.', 'API Key'],
              ],
            },
          ],
        },
      },
      {
        id: 'integrations-crm-sales',
        title: 'CRM & Sales',
        whatItIs: 'Keep your CRM in step with your outreach.',
        table: {
          columns: PROVIDER_COLUMNS,
          groups: [
            {
              rows: [
                ['Salesforce', 'Sync contacts and track opportunities.', 'Access Token + Instance URL'],
                ['HubSpot', 'Sync contacts, deals, and marketing automation.', 'Private App Token'],
                ['Pipedrive', 'Deal and pipeline tracking.', 'API Token'],
              ],
            },
          ],
        },
      },
      {
        id: 'integrations-calendar',
        title: 'Calendar',
        whatItIs: 'Book meetings straight from a workflow.',
        table: {
          columns: PROVIDER_COLUMNS,
          groups: [
            {
              rows: [
                ['Google Calendar', 'Schedule meetings on a Google calendar.', 'Service Account JSON'],
                ['Outlook Calendar', 'Schedule meetings on Microsoft 365.', 'Tenant ID + Client ID + Secret'],
                ['Calendly', 'Share booking links / scheduling pages.', 'Personal Access Token'],
              ],
            },
          ],
        },
      },
      {
        id: 'integrations-meetings-communication',
        title: 'Meetings & Communication',
        whatItIs: 'Video calls and team notifications.',
        table: {
          columns: PROVIDER_COLUMNS,
          groups: [
            {
              rows: [
                ['Zoom', 'Create and list Zoom meetings.', 'Account ID + Client ID + Secret'],
                ['Microsoft Teams', 'Team notifications and updates.', 'Incoming Webhook URL'],
                ['Slack', 'Notifications, alerts, team updates.', 'Incoming Webhook URL'],
                ['Google Meet', 'Meeting scheduling / video calls.', 'Coming soon'],
              ],
            },
          ],
        },
      },
      {
        id: 'integrations-voice-ai',
        title: 'Voice & AI',
        whatItIs: 'Reach contacts by phone.',
        table: {
          columns: PROVIDER_COLUMNS,
          groups: [
            {
              rows: [
                ['Twilio', 'SMS follow-ups and phone verification.', 'Account SID + Auth Token + Phone Number'],
              ],
            },
          ],
        },
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Your home screen - a quick read on account activity and what to do next.',
    articles: [
      {
        id: 'dashboard-reading',
        title: 'Reading your dashboard',
        whatItIs: 'The dashboard is your home screen - a quick read on account activity and what to do next.',
        steps: [
          'When your account is new, it guides you through setup (connect a mailbox, add contacts, build and launch a workflow).',
          "Once you're sending, it summarises your key metrics and recent activity, with shortcuts into campaigns and analytics.",
          'Use it as your daily starting point and jump off to the module you need.',
        ],
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'How your emails are performing, across five tabs.',
    articles: [
      {
        id: 'analytics-overview',
        title: 'Analytics overview',
        whatItIs: 'Analytics shows how your emails are performing, across five tabs.',
        steps: [
          'Overview: headline metrics across all campaigns - emails sent, open rate, click rate, reply rate, bounce rate, plus engagement trends and reply sentiment.',
          'Campaigns: performance per campaign (click in for per-campaign detail, including per sending inbox).',
          'Contacts: engagement at the contact level.',
          'Deliverability: delivered, bounced, and spam/complaint signals, with SPF/DKIM/DMARC status per sending domain.',
          'Replies: reply volume and quality, broken down by sentiment and intent.',
        ],
        callout: {
          tone: 'tip',
          text: 'Watch open and reply rates per sending inbox - if one mailbox dips, pause it before it drags down the whole campaign.',
        },
      },
    ],
  },
  {
    id: 'inbox',
    title: 'Inbox & Replies',
    description: 'Replies from your connected mailboxes in one place, already classified.',
    articles: [
      {
        id: 'inbox-manage-replies',
        title: 'Manage replies in the Inbox',
        whatItIs: 'The Inbox brings replies from your connected mailboxes into one place, already classified.',
        steps: [
          'Replies are automatically tagged by sentiment and intent (e.g. interested, not interested, out-of-office).',
          'Filter and sort to find the replies worth acting on first.',
          'When a contact replies, Beevelope can stop sending them further automated emails so you can take over the conversation.',
        ],
        callout: {
          tone: 'note',
          text: 'Reply detection requires IMAP / mailbox monitoring to be enabled on the sending mailbox (set when you add the mailbox).',
        },
      },
    ],
  },
  {
    id: 'billing',
    title: 'Account & Billing',
    description: 'Beevelope runs on a subscription plus a credit system.',
    articles: [
      {
        id: 'billing-credits-and-subscription',
        title: 'Credits and subscription',
        whatItIs: 'Beevelope runs on a subscription plus a credit system. Credits power the AI and data features.',
        steps: [
          'Credits are used for the paid AI/data actions - contact enrichment, AI-generated emails, AI-generated collateral, and reply sentiment analysis.',
          'Your plan and credit balance are shown in the Billing section.',
          'New accounts start with a free trial (200 credits, no card required); annual plans are discounted.',
        ],
        callout: {
          tone: 'tip',
          text: "Sending itself doesn't burn credits - the AI and enrichment steps do. Plan credits around how much AI generation and enrichment you expect to use.",
        },
      },
    ],
  },
]
